// Storage read-out screen (STO-3, M41/M42). Shows the bytes of persisted player-caused
// data per world plus the total against the account quota, warning past STO_WARN_FRACTION.
// The data comes through `game.storageUsage()`, which is the local IndexedDB scan solo
// (M41) and the server account read-out in net mode (M42) — the same panel either way.
// No payment code anywhere (STO-4).
import { h } from '../h';
import type { Game } from '../../boot';
import { formatBytes, type UsageReport } from '../../world/usage';

export function openUsageScreen(game: Game): void {
  game.uiOpen = true;
  document.exitPointerLock();

  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-usage', 'data-testid': 'usage-screen' });
  overlay.append(panel);
  document.body.append(overlay);

  const close = (): void => { overlay.remove(); game.uiOpen = false; };
  overlay.onclick = (e) => { if (e.target === overlay) close(); };

  const status = h('div', { class: 'bb-script-status bb-script-ok', 'data-testid': 'usage-status' }, 'Measuring…');
  const listEl = h('div', { class: 'bb-usage-list' });
  const closeBtn = h('button', { class: 'bb-btn', 'data-testid': 'usage-close' }, 'Close') as HTMLButtonElement;
  closeBtn.onclick = close;

  panel.append(
    h('div', { class: 'bb-window-title' }, '💾 Storage'),
    status,
    listEl,
    h('div', { class: 'bb-row' }, closeBtn),
  );

  const render = (r: UsageReport): void => {
    const pct = Math.round(r.fraction * 100);
    status.textContent = `${formatBytes(r.totalBytes)} of ${formatBytes(r.quotaBytes)} used (${pct}%)`;
    status.className = `bb-script-status ${r.warn ? 'bb-script-err' : 'bb-script-ok'}`;
    listEl.innerHTML = '';
    if (r.worlds.length === 0) { listEl.append(h('div', { class: 'bb-muted' }, 'No worlds yet.')); return; }
    const max = Math.max(1, ...r.worlds.map((w) => w.bytes));
    for (const w of r.worlds) {
      const bar = h('div', { class: 'bb-usage-bar' });
      const fill = h('div', { class: 'bb-usage-fill' }) as HTMLElement;
      fill.style.width = `${Math.max(2, Math.round((w.bytes / max) * 100))}%`;
      bar.append(fill);
      listEl.append(h('div', { class: 'bb-usage-item', 'data-testid': `usage-world-${w.id}` },
        h('div', { class: 'bb-usage-name' }, w.name),
        bar,
        h('div', { class: 'bb-usage-bytes' }, formatBytes(w.bytes)),
      ));
    }
  };

  void game.storageUsage().then(render).catch(() => { status.textContent = 'Could not measure storage.'; });
}
